import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import {
  LayoutDashboard, Inbox, Users, Building2, BookOpen, Calendar,
  Bell, Plug, Briefcase, Bot, Activity, Settings as SettingsIcon,
  Search, ArrowRight, CornerDownLeft, ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/cn";

interface Item {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon?: React.ReactNode;
  action: () => void;
  /** Lower = higher priority. */
  weight?: number;
}

interface ContactLite {
  id: string;
  display_name: string | null;
  primary_phone: string | null;
}
interface PropertyLite {
  id: string;
  name: string;
  area: string;
}

const NAV_ITEMS: { to: string; label: string; icon: React.ReactNode; group: string }[] = [
  { to: "/",              label: "Dashboard",      icon: <LayoutDashboard className="h-4 w-4" />, group: "Go to" },
  { to: "/inbox",         label: "Inbox",          icon: <Inbox className="h-4 w-4" />,           group: "Go to" },
  { to: "/contacts",      label: "Contacts",       icon: <Users className="h-4 w-4" />,           group: "Go to" },
  { to: "/properties",    label: "Properties",     icon: <Building2 className="h-4 w-4" />,       group: "Go to" },
  { to: "/visits",        label: "Visits",         icon: <Calendar className="h-4 w-4" />,        group: "Go to" },
  { to: "/notifications", label: "Notifications",  icon: <Bell className="h-4 w-4" />,            group: "Go to" },
  { to: "/activity",      label: "Activity log",   icon: <Activity className="h-4 w-4" />,        group: "Go to" },
  { to: "/business",      label: "Business",       icon: <Briefcase className="h-4 w-4" />,       group: "Go to" },
  { to: "/ai-bot",        label: "AI Bot",         icon: <Bot className="h-4 w-4" />,             group: "Go to" },
  { to: "/knowledge",     label: "Knowledge",      icon: <BookOpen className="h-4 w-4" />,        group: "Go to" },
  { to: "/connections",   label: "Connections",    icon: <Plug className="h-4 w-4" />,            group: "Go to" },
  { to: "/settings",      label: "Settings",       icon: <SettingsIcon className="h-4 w-4" />,    group: "Go to" },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Global keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isToggle =
        (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isToggle) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Close on route change.
  useEffect(() => {
    if (open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Reset query + focus on open.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      // Wait a tick so the input is mounted.
      const r = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(r);
    }
  }, [open]);

  // Fetch contacts + properties only while the palette is open.
  const { data: contacts = [] } = useQuery({
    queryKey: ["palette", "contacts"],
    queryFn: () => api.get<ContactLite[]>("/api/contacts?limit=100"),
    enabled: open,
    staleTime: 30_000,
  });
  const { data: properties = [] } = useQuery({
    queryKey: ["palette", "properties"],
    queryFn: () => api.get<PropertyLite[]>("/api/properties?limit=100"),
    enabled: open,
    staleTime: 30_000,
  });

  // Build the item universe.
  const allItems = useMemo<Item[]>(() => {
    const items: Item[] = NAV_ITEMS.map((n) => ({
      id: `nav:${n.to}`,
      label: n.label,
      group: n.group,
      icon: n.icon,
      action: () => navigate(n.to),
      weight: 1,
    }));
    for (const c of contacts) {
      items.push({
        id: `contact:${c.id}`,
        label: c.display_name ?? "(no name)",
        hint: c.primary_phone ?? undefined,
        group: "Contacts",
        icon: <Users className="h-4 w-4 text-slate-400" />,
        action: () => navigate(`/contacts/${c.id}`),
        weight: 2,
      });
    }
    for (const p of properties) {
      items.push({
        id: `property:${p.id}`,
        label: p.name,
        hint: p.area,
        group: "Properties",
        icon: <Building2 className="h-4 w-4 text-slate-400" />,
        action: () => navigate("/properties"),
        weight: 2,
      });
    }
    return items;
  }, [contacts, properties, navigate]);

  // Filter + rank.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Default: show navigation first.
      return allItems.filter((i) => i.group === "Go to").slice(0, 20);
    }
    return allItems
      .map((i) => ({ i, score: scoreMatch(q, i) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30)
      .map(({ i }) => i);
  }, [allItems, query]);

  // Group items by their `group` field, preserving filtered order.
  const groups = useMemo(() => {
    const map = new Map<string, Item[]>();
    for (const i of filtered) {
      if (!map.has(i.group)) map.set(i.group, []);
      map.get(i.group)!.push(i);
    }
    return Array.from(map.entries());
  }, [filtered]);

  // Flat list for keyboard nav.
  const flat = useMemo(() => groups.flatMap(([, items]) => items), [groups]);

  // Keyboard nav inside the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flat[activeIdx];
        if (item) item.action();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, flat, activeIdx]);

  // Reset active index whenever filtered changes.
  useEffect(() => {
    setActiveIdx(0);
  }, [filtered.length, query]);

  // Scroll active item into view.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/40 px-4 pt-16 sm:pt-24"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-label="Command palette"
    >
      <div
        className="mx-auto w-full max-w-xl bg-brand-surface rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, contacts, properties…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
          />
          <kbd className="hidden sm:inline-block text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="max-h-80 overflow-y-auto py-1">
          {flat.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              No results for "{query}".
            </div>
          ) : (
            groups.map(([group, items]) => (
              <div key={group}>
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <ChevronDown className="h-3 w-3" /> {group}
                </div>
                {items.map((item) => {
                  const idx = flat.indexOf(item);
                  const active = idx === activeIdx;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-idx={idx}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => item.action()}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
                        active ? "bg-slate-100" : "hover:bg-slate-50",
                      )}
                    >
                      {item.icon ?? <span className="h-4 w-4" />}
                      <span className="flex-1 min-w-0 truncate text-slate-900">{item.label}</span>
                      {item.hint && (
                        <span className="text-xs text-slate-500 truncate max-w-[140px]">{item.hint}</span>
                      )}
                      {active && <ArrowRight className="h-3.5 w-3.5 text-slate-400" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 text-[11px] text-slate-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="border border-slate-200 rounded px-1">↑↓</kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="border border-slate-200 rounded px-1 inline-flex items-center"><CornerDownLeft className="h-3 w-3" /></kbd> open
            </span>
          </div>
          <span>{flat.length} result{flat.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Simple fuzzy match scoring: rewards prefix, then substring, then chars.
// ---------------------------------------------------------------------------
function scoreMatch(q: string, item: Item): number {
  const hay = `${item.label} ${item.hint ?? ""}`.toLowerCase();
  if (hay.startsWith(q)) return 100 - (item.weight ?? 0);
  if (hay.includes(q))   return 60  - (item.weight ?? 0);
  // Token match - every word in q must be present
  const tokens = q.split(/\s+/);
  if (tokens.every((t) => hay.includes(t))) return 30 - (item.weight ?? 0);
  return 0;
}
